import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useUrlState } from '../nav/urlState';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { todayInWarsaw } from '../events/calendar';
import { PrivateTasks } from '../private-tasks/PrivateTasksPage';
import { useAssignedTasksState } from './access';
import { AssigneeField } from './AssigneeField';
import { TaskDialog } from './TaskDialog';
import { canTickOff, formatDue, isOverdue, scopeOf, statusText, TASKS_KEY, TASKS_RESOURCE, type Task } from './types';
import './tasks.css';
import { useUnsavedGuard } from '../ui/unsaved';

type Status = 'open' | 'done' | 'all';
const FILTERS: { id: Status; label: string }[] = [
  { id: 'open', label: 'Do zrobienia' },
  { id: 'done', label: 'Zrobione' },
  { id: 'all', label: 'Wszystkie' },
];

/**
 * "Zadania" with two tabs (zadania prywatne, 2026-10-23): "Firmowe" (company tasks) and "Moje zadania" (the user's
 * private organizer, `?widok=moje`). Everybody has "Moje zadania"; the company tab shows to whoever may see company
 * tasks or has one given to them. Without `widok` a link to a company task (`?zadanie=…`, the bell) opens "Firmowe";
 * otherwise a user without company tasks (no grant, none given) starts on "Moje zadania".
 */
export function TasksPage() {
  const { me } = useMe();
  const { assigned, loading } = useAssignedTasksState(me);
  const { params, update } = useUrlState();
  if (!me) return null;
  const granted = can(me, TASKS_RESOURCE, 'view');
  const widok = params.get('widok');
  // the count of tasks given to me decides the first tab: wait for it rather than flick from one tab to the other (M6)
  const waiting = !granted && loading && widok === null && !params.has('zadanie');
  const { mine, tabs } = taskTabs(widok, params.has('zadanie'), granted, assigned);
  // a tab starts clean: the other tab's filters and open task stay behind
  const show = (moje: boolean) => update({ widok: moje ? 'moje' : granted ? null : 'firmowe', status: null, moje: null, zadanie: null, lista: null });
  return (
    <section className="page wide tasks-page" data-feature-id={mine ? 'private-tasks.list' : 'company-tasks.list'}>
      <div className="page-head">
        <h1>Zadania</h1>
      </div>
      {waiting ? (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      ) : (
        <>
          {tabs && <TaskTabs mine={mine} onShow={show} />}
          <div id={TASK_PANEL_ID} {...(tabs ? { role: 'tabpanel', 'aria-labelledby': mine ? 'task-tab-moje' : 'task-tab-firmowe' } : {})}>
            {mine ? <PrivateTasks /> : <CompanyTasks />}
          </div>
        </>
      )}
    </section>
  );
}

export const TASK_PANEL_ID = 'task-tab-panel';

/** The two tabs (WAI-ARIA tabs: the chosen one in the Tab order, ← → Home End switch and move the focus). */
export function TaskTabs({ mine, onShow }: { mine: boolean; onShow: (mine: boolean) => void }) {
  const list = useRef<HTMLDivElement>(null);
  const go = (toMine: boolean) => {
    onShow(toMine);
    list.current?.querySelector<HTMLElement>(toMine ? '#task-tab-moje' : '#task-tab-firmowe')?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.key === 'ArrowRight' || e.key === 'ArrowLeft' ? !mine : e.key === 'Home' ? false : e.key === 'End' ? true : null;
    if (target === null) return;
    e.preventDefault();
    go(target);
  };
  const tab = (id: string, label: string, selected: boolean, toMine: boolean) => (
    <button id={id} role="tab" type="button" aria-selected={selected} aria-controls={TASK_PANEL_ID} tabIndex={selected ? 0 : -1} onClick={() => onShow(toMine)}>
      {label}
    </button>
  );
  return (
    <div ref={list} role="tablist" aria-label="Zadania" className="segment pt-tabs" onKeyDown={onKeyDown}>
      {tab('task-tab-firmowe', 'Firmowe', !mine, false)}
      {tab('task-tab-moje', 'Moje zadania', mine, true)}
    </div>
  );
}

/** Which tab shows (`mine`: "Moje zadania") and whether the tabs show at all (see `TasksPage`). */
export function taskTabs(widok: string | null, companyTaskLink: boolean, granted: boolean, assigned: boolean): { mine: boolean; tabs: boolean } {
  // whoever has company tasks (granted, or given one) starts on "Firmowe" as before
  const mine = widok === 'moje' || (widok !== 'firmowe' && !companyTaskLink && !granted && !assigned);
  return { mine, tabs: granted || assigned || !mine };
}

/**
 * "Zadania firmowe" (P7, owner remarks 4/4, 4/15): company matters outside events — the warehouse, rubbish, clothes. A
 * list with the due day (red when overdue), the person responsible and done or not; quick adding on top (title, day,
 * person, Enter); ticking off in the row; everything else in the task's dialog (`?zadanie=<id>`, also the bell's link).
 */
function CompanyTasks() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  // P13: the filters and the open task live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const statusParam = params.get('status');
  const status: Status = statusParam === 'done' || statusParam === 'all' ? statusParam : 'open';
  const setStatus = (s: Status) => update({ status: s === 'open' ? null : s });
  const mine = params.get('moje') === '1';
  const setMine = (m: boolean) => update({ moje: m ? '1' : null });
  const openId = params.get('zadanie');
  // "Nowe zadanie firmowe" on "Wydarzenia" (owner 20.10 23:37) leads here with `?nowe=1`: the quick form, ready to type
  const [fresh] = useState(() => params.get('nowe') === '1');
  // ... with the day picked in the calendar as the due day (owner 21.10 00:44)
  const [freshDay] = useState(() => {
    const d = params.get('dzien');
    return fresh && d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  });
  useEffect(() => {
    if (fresh) update({ nowe: null, dzien: null });
    // only once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const today = todayInWarsaw();
  const list = useQuery({
    queryKey: [...TASKS_KEY, 'list', status, mine],
    placeholderData: keepPreviousData,
    queryFn: () => api<{ tasks: Task[] }>(`/api/company-tasks?status=${status}${mine ? '&mine=1' : ''}`),
  });
  const tick = useMutation({
    mutationFn: (t: Task) => api<Task>(`/api/company-tasks/${t.id}`, { method: 'PATCH', body: { version: t.version, done: !t.done } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: TASKS_KEY }),
  });
  // the box changes at once, in the same tick as the click (a controlled checkbox would otherwise snap back for a
  // frame until the mutation's onMutate runs); the row's "who and when" comes with the server's answer
  const toggle = (t: Task) => {
    void queryClient.cancelQueries({ queryKey: [...TASKS_KEY, 'list'] });
    queryClient.setQueriesData<{ tasks: Task[] }>({ queryKey: [...TASKS_KEY, 'list'] }, (data) =>
      data ? { tasks: data.tasks.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)) } : data,
    );
    tick.mutate(t);
  };
  if (!me) return null;
  const seeAll = scopeOf(me, 'view') === 'all';
  const mayTick = (t: Task) => canTickOff(me, t);
  const showAssignee = fieldVisible(me, TASKS_RESOURCE, 'assigneeUserId');
  const open = (id: string) => update({ zadanie: id });
  const close = () => update({ zadanie: null });
  const tasks = list.data?.tasks ?? [];
  const error = list.error ?? tick.error;

  return (
    <div>
      <p className="lede">
        {seeAll ? 'Sprawy firmy poza wydarzeniami: magazyn, wywóz śmieci, zamówienia. Widzisz wszystkie zadania; osoba, której zlecisz zadanie, widzi tylko swoje.' : 'Zadania zlecone Tobie. Zaznacz, gdy zrobione; w zadaniu możesz dopisać notatkę.'}
      </p>
      {scopeOf(me, 'create') && <QuickAdd focus={fresh} day={freshDay} />}
      <div className="ct-filters">
        <div className="segment" role="group" aria-label="Pokaż">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" aria-pressed={status === f.id} onClick={() => setStatus(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        {seeAll && (
          <div className="segment" role="group" aria-label="Czyje">
            <button type="button" aria-pressed={mine} onClick={() => setMine(!mine)}>
              Moje
            </button>
          </div>
        )}
      </div>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {error && <p className="alert" role="alert">{error instanceof ApiError ? error.message : 'Nie udało się wczytać zadań'}</p>}
      {list.data && tasks.length === 0 && <p className="muted">{status === 'open' ? 'Nie ma zadań do zrobienia.' : 'Nie ma takich zadań.'}</p>}
      {tasks.length > 0 && (
        <div className="table-wrap">
          <table className="table ct-table" aria-label="Zadania">
            <thead>
              <tr>
                <th scope="col" className="ct-check-col">
                  <span className="visually-hidden">Zrobione</span>
                </th>
                <th scope="col">Tytuł</th>
                <th scope="col">Termin</th>
                {showAssignee && <th scope="col">Odpowiedzialny</th>}
                <th scope="col" className="wide-only">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => {
                const overdue = isOverdue(t, today);
                return (
                  <tr key={t.id} className={t.done ? 'ct-is-done' : undefined}>
                    <td className="ct-check-col">
                      {t.done !== undefined && (
                        <input type="checkbox" aria-label={`Zrobione: ${t.title}`} checked={t.done} disabled={!mayTick(t) || tick.isPending} onChange={() => toggle(t)} />
                      )}
                    </td>
                    <th scope="row">
                      <button type="button" className="ct-title" onClick={() => open(t.id)}>
                        {t.title}
                      </button>
                      {t.repeat && (
                        <span className="ct-repeat" title={t.repeat === 'weekly' ? 'Powtarza się co tydzień' : 'Powtarza się co miesiąc'}>
                          {' '}
                          ↻
                        </span>
                      )}
                    </th>
                    <td className={overdue ? 'ct-overdue' : undefined}>
                      {formatDue(t)}
                      {overdue && <span className="visually-hidden"> (zaległe)</span>}
                    </td>
                    {showAssignee && <td>{t.assigneeName ?? '—'}</td>}
                    <td className="wide-only">{t.done !== undefined ? statusText(t) : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {openId && <TaskDialog me={me} taskId={openId} onClose={close} />}
    </div>
  );
}

/** Title, day and person in one row; Enter adds. The day and the person stay for the next task. `focus`: the title at once. */
function QuickAdd({ focus = false, day = null }: { focus?: boolean; day?: string | null }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [dueDay, setDueDay] = useState(() => day ?? todayInWarsaw());
  const [assignee, setAssignee] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focus) input.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Task>('/api/company-tasks', { method: 'POST', body }),
    onSuccess: async () => {
      setTitle('');
      input.current?.focus();
      await queryClient.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
  const body = () => ({ title: title.trim(), dueDay, ...(assignee && me && fieldVisible(me, TASKS_RESOURCE, 'assigneeUserId') ? { assigneeUserId: assignee } : {}) });
  // owner 2026-10-23 21:14: a title typed and not added: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: title.trim() !== '',
    save: async () => {
      await add.mutateAsync(body());
      return true;
    },
  });
  if (!me) return null;
  const visible = (f: string) => fieldVisible(me, TASKS_RESOURCE, f);
  const issues = add.error instanceof ApiError ? fieldIssues(add.error) : [];
  return (
    <form
      ref={guardRef}
      className="ct-quick"
      aria-label="Szybkie dodawanie zadania"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        add.mutate(body());
      }}
    >
      <input ref={input} className="ct-quick-title" aria-label="Tytuł zadania" placeholder="Nowe zadanie, np. Wywóz śmieci" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} autoComplete="off" />
      <input type="date" aria-label="Termin" value={dueDay} onChange={(e) => setDueDay(e.target.value)} required />
      {visible('assigneeUserId') && scopeOf(me, 'create') === 'all' && <AssigneeField me={me} value={assignee} onChange={setAssignee} compact />}
      <button className="btn primary" type="submit" disabled={add.isPending || !title.trim()}>
        Dodaj
      </button>
      {add.error && (
        <p className="alert ct-quick-error" role="alert">
          {issues.length > 0 ? issues.map((i) => i.message).join('. ') : add.error instanceof ApiError ? add.error.message : 'Nie udało się dodać zadania'}
        </p>
      )}
    </form>
  );
}
